import * as XLSX from "xlsx";
import * as XLSXSH from "sheetjs-style";
import { saveAs } from "file-saver";
import moment from "moment";
import { toast } from "react-toastify";
import { MANUAL_QC_FLAGS, ML_SOFTWARE_QC_FLAGS } from "../../../utils/statusConfig";
import {
  downloadFileFromS3,
  getPresignedURL,
  uploadFileToS3,
} from "../../../utils/helpers";
import { s3AutomationPrivate } from "../../../utils/aws";

// eslint-disable-next-line import/prefer-default-export
export const RuleStatusMap = {
  Active: {
    value: true,
    label: "Active",
    color: "#1EC04A",
    backgroundColor: "#E9FFEF",
    borderColor: "#1EC04A",
  },
  Inactive: {
    value: false,
    label: "Inactive",
    color: "#E11903",
    backgroundColor: "#FFE1E1",
    borderColor: "#E11903",
  },
};

export const userCorrectionMap = {
  1: "Approved",
  2: "Rejected",
  Approved: 1,
  Rejected: 2,
};

export const qcCustomerRuleScopes = [
  { label: "Sample", value: "sample" },
  { label: "Generic", value: "generic" },
];

export const regularTestMatchScopeOptions = [
  { label: "Test Category", value: "test_category" },
  { label: "Display Name", value: "display_name" },
  { label: "Internal Name", value: "internal_name" },
];

export const regularTestsValidationRule = ({ getFieldValue }) => ({
  validator: (_, val) => {
    if (Array.isArray(val) && val.length > 0) {
      return Promise.resolve();
    }
    const message = getFieldValue("match_scope") === "test_category"
      ? "Select a test category"
      : "Select at least one regular test";
    return Promise.reject(new Error(message));
  },
});

export function getRegularTestName(spec) {
  if (!spec || typeof spec !== "object") return null;
  if (spec.match_scope && spec[spec.match_scope]) {
    return spec[spec.match_scope] ?? null;
  }
  return spec.display_name ?? spec.internal_name ?? null;
}

export function groupRegularTestsByCategory(regularTests) {
  if (!regularTests || !Array.isArray(regularTests) || regularTests.length === 0) {
    return [];
  }
  const grouped = regularTests.reduce((acc, spec) => {
    if (!spec) return acc;
    const category = spec.test_category;
    const testName = getRegularTestName(spec);
    if (!testName) return acc;
    if (!acc[category]) acc[category] = new Set();
    acc[category].add(testName);
    return acc;
  }, {});
  return Object.entries(grouped).map(([category, names]) => ({
    category,
    names: names.size === 0 ? "-" : Array.from(names).join(", "),
  }));
}

export function hasGroupedRegularTests(regularTests) {
  return groupRegularTestsByCategory(regularTests).length > 0;
}

/**
 * Normalize API `regular_tests` into CategorizedTestSelector value objects.
 * Each item: { match_scope, test_category, internal_name, display_name, id? }
 */
export function regularTestsToSelectedTests(regularTests, matchScope = "display_name") {
  if (!Array.isArray(regularTests) || regularTests.length === 0) {
    return [];
  }

  return regularTests.reduce((selectedTests, item) => {
    if (!item || typeof item !== "object") {
      return selectedTests;
    }

    const normalizedMatchScope = item.match_scope ?? matchScope;
    const internalName = String(item.internal_name ?? "").trim();
    const displayName = String(item.display_name ?? "").trim();
    const testCategory = String(item.test_category ?? "").trim();

    if (!testCategory || (normalizedMatchScope !== "test_category" && !internalName)) {
      return selectedTests;
    }

    const id = item.id;
    selectedTests.push({
      match_scope: normalizedMatchScope,
      test_category: testCategory,
      internal_name: internalName,
      display_name: displayName,
      ...(id != null && !Number.isNaN(Number(id)) ? { id: Number(id) } : {}),
    });

    return selectedTests;
  }, []);
}

export function buildRegularTestsPayload(regularTests, matchScope = "display_name") {
  const normalizedTests = (regularTests ?? []).map(({ id, ...test }) => ({
    ...test,
    match_scope: matchScope,
  }));

  if (matchScope !== "test_category") {
    return normalizedTests;
  }

  const testCategory = normalizedTests.find((test) => test.test_category)?.test_category;
  return testCategory ? [{
    match_scope: matchScope,
    test_category: testCategory,
    internal_name: "",
    display_name: "",
  }] : [];
}

/**
 * Split QC status reason string into array
 */
export const makeReasonArray = (reason) => {
  if (!reason) return [];
  let inputString = reason;
  while (inputString.charAt(0) === "\n") {
    inputString = inputString.slice(1);
  }
  while (inputString.charAt(inputString.length - 1) === "\n") {
    inputString = inputString.slice(0, -1);
  }
  return inputString.split("\n");
};

export const QC_OPTIONS = [
  { label: "Pass", value: "1" },
  { label: "Hold", value: "2" },
  { label: "Create Retest", value: "3" },
  { label: "RCA", value: "4" },
];

// export const DEFAULT_ADDITIONAL_OPTIONS = [
//   { label: "Create Retest", value: "generate_retest", disabled: false },
//   { label: "Contact Customer", value: "contact_customer", disabled: false },
// ];

export const DEFAULT_ADDITIONAL_OPTIONS = [];

export const HISTORY_COLUMNS = [
  {
    title: "Sample ID",
    dataIndex: "sample_id",
    key: "sample_id",
  },
  {
    title: "Sample Type",
    dataIndex: "sample_type",
    key: "sample_type",
  },
  {
    title: "Result",
    dataIndex: "result",
    key: "result",
  },
  {
    title: "QC", dataIndex: "ml_soft_qc_status", key: "ml_soft_qc_status", qcFlagKey: "ml_soft_qc_result",
  },
  {
    title: "AI QC", dataIndex: "ml_qc_status", key: "ml_qc_status", qcFlagKey: "ml_qc_result",
  },
  {
    title: "Software QC", dataIndex: "qc_status", key: "qc_status", qcFlagKey: "qc_result",
  },
  {
    title: "Manual QC", dataIndex: "manual_qc_status", key: "manual_qc_status", qcFlagKey: "manual_qc_result", qcType: "MANUAL_QC",
  },
];

// QC_EXPORT_COLUMNS is used to define the columns for the QC export Excel file
const QC_EXPORT_COLUMNS = [
  { key: "customer_id", header: "Customer Id" },
  { key: "workorder_id", header: "WO Id" },
  { key: "genista_id", header: "Genista Id" },
  { key: "sample_type", header: "Sample Type" },
  { key: "sample_id", header: "Sample Id" },
  { key: "test_category", header: "Test Category" },
  { key: "analysis", header: "Test Name" },
  { key: "result", header: "Result" },
  { key: "manual_qc_assigned_to_name", header: "QC Manual Assigned" },
  { key: "manual_qc_result", header: "QC Manual Status" },
  { key: "manual_qc_status", header: "QC Manual Response" },
  { key: "ml_qc_result", header: "QC ML Status" },
  { key: "ml_qc_status", header: "QC ML Response" },
  { key: "qc_result", header: "QC Software Status" },
  { key: "qc_status", header: "QC Software Response" },
];

const buildQcExportRow = (item) => {
  const test = item.sample.test;
  const qc = test.qc;
  const manual = qc.manual;
  const ml = qc.ml;
  const software = qc.software;
  const manualQcFlag = MANUAL_QC_FLAGS[manual.manual_qc_result];
  const mlQcFlag = ML_SOFTWARE_QC_FLAGS[ml.ml_qc_result];
  const softwareQcFlag = ML_SOFTWARE_QC_FLAGS[software.qc_result];

  return {
    customer_id: item.customer.customer_id,
    workorder_id: item.workorder.workorder_id,
    sample_id: item.sample.sample_id,
    sample_type: item.sample.type,
    genista_id: item.sample.genista_id,
    test_category: test.test_category,
    analysis: test.analysis,
    result: test.result,
    manual_qc_assigned_to_name: manual.manual_qc_assigned_to_name,
    manual_qc_result: manualQcFlag.status,
    manual_qc_status: manual.manual_qc_status,
    ml_qc_result: mlQcFlag.status,
    ml_qc_status: ml.ml_qc_status,
    qc_result: softwareQcFlag.status,
    qc_status: software.qc_status,
  };
};

export const buildQcExportRows = (apiResult) => {
  if (!Array.isArray(apiResult)) {
    return [];
  }
  return apiResult.map(buildQcExportRow);
};

// buildQcExportFileName is used to build the file name for the QC export Excel file
export const buildQcExportFileName = () => {
  const date = new Date();
  return `QC_Export_${moment(date).format("MM/DD/YYYY_HHmmss")}.xlsx`;
};

// downloadQcExportExcel is used to download the QC export Excel file
export const downloadQcExportExcel = (rows, fileName) => {
  const headerRow = QC_EXPORT_COLUMNS.map((column) => column.header);
  const dataRows = rows.map((row) => QC_EXPORT_COLUMNS.map((column) => row[column.key] ?? ""));
  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
  worksheet["!cols"] = QC_EXPORT_COLUMNS.map(() => ({ wch: 24 }));

  QC_EXPORT_COLUMNS.forEach((_, colIndex) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = { font: { bold: true } };
    }
  });

  const workbook = { Sheets: { "QC Export": worksheet }, SheetNames: ["QC Export"] };
  const buffer = XLSXSH.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: "true",
  });
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, fileName);
};

const RCA_ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx", ".xls", ".xlsx"];

const RCA_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export const RCA_UPLOAD_ACCEPT = [
  ...RCA_ALLOWED_EXTENSIONS,
  ...RCA_ALLOWED_MIME_TYPES,
].join(",");

const getFileExtension = (fileName = "") => {
  const lowerName = fileName.toLowerCase();
  const extensionIndex = lowerName.lastIndexOf(".");
  if (extensionIndex < 0) return "";
  return lowerName.slice(extensionIndex);
};

const isAllowedRcaDocument = (file) => {
  if (!file) return false;
  const extension = getFileExtension(file.name || "");
  if (RCA_ALLOWED_EXTENSIONS.includes(extension)) return true;
  return RCA_ALLOWED_MIME_TYPES.includes(file.type);
};

export const validateRcaDocumentFile = (file) => {
  if (!isAllowedRcaDocument(file)) {
    return {
      valid: false,
      message: "Only PDF, Word (.doc, .docx), and Excel (.xls, .xlsx) files are allowed.",
    };
  }
  return { valid: true, message: "" };
};

export const buildRcaS3Key = (tisPk, fileName) => {
  const originalFileName = fileName || "document";
  const extension = getFileExtension(originalFileName);
  const nameWithoutExtension = extension
    ? originalFileName.slice(0, -extension.length)
    : originalFileName;
  const safeName = nameWithoutExtension.replace(/[^a-zA-Z0-9_-]/g, "_");
  const uploadTimestamp = moment().format("YYYY_MM_DD_HH_mm_ss");
  return `media/rca/${tisPk}/${safeName}_RCA_${uploadTimestamp}${extension}`;
};

/** Upload an RCA document to automation private S3 and return the object key. */
export const uploadRcaDocument = async (file, tisPk) => {
  const validation = validateRcaDocumentFile(file);
  if (!validation.valid) {
    return { success: false, message: validation.message, path: "" };
  }

  const key = buildRcaS3Key(tisPk, file.name);
  const uploadResult = await uploadFileToS3(
    { file, key, type: file.type || "application/octet-stream" },
    s3AutomationPrivate,
  );

  if (!uploadResult.success) {
    return { success: false, message: "Failed to upload RCA document.", path: "" };
  }

  return { success: true, message: "Uploaded", path: uploadResult.path };
};

/** Download an RCA document via existing presign helpers. */
export const downloadRcaDocument = async (s3Key, signal = null) => {
  if (!s3Key) {
    return { success: false, message: "File path is not available.", aborted: false };
  }

  const presignedData = await getPresignedURL({
    action: "get",
    urls: [{
      Bucket: s3AutomationPrivate.bucket,
      Name: s3Key,
      Region: s3AutomationPrivate.region,
    }],
  }, signal);

  if (presignedData.aborted) {
    return { success: false, message: "", aborted: true };
  }

  if (!presignedData.success || !presignedData.result?.[s3Key]) {
    return { success: false, message: "Failed to get presigned URL for download.", aborted: false };
  }

  const fileName = s3Key.split("/").pop() || s3Key;
  const downloadResult = await downloadFileFromS3(
    s3Key,
    s3AutomationPrivate,
    fileName,
    presignedData.result[s3Key],
    signal,
  );

  if (!downloadResult.success && !downloadResult.aborted) {
    return { success: false, message: `Failed to download ${fileName}`, aborted: false };
  }

  return {
    success: downloadResult.success,
    message: "",
    aborted: Boolean(downloadResult.aborted),
  };
};

/** Shared click handler: download with toast feedback. */
export const handleRcaDocumentDownload = async (s3Key, signal = null) => {
  const result = await downloadRcaDocument(s3Key, signal);
  if (!result.success && !result.aborted && result.message) {
    toast.error(result.message);
  }
  return result;
};
